import { Heart } from "lucide-react";
import { useEffect } from "react";
import { Link } from "react-router-dom";
import { PageHeader } from "@/components/PageHeader";

const About = () => {
  useEffect(() => {
    document.title = "About Quizrun — Founders, Mission & Story";
    const meta = document.querySelector('meta[name="description"]');
    const content = "Quizrun is a sport where running and thinking matter equally. Founded by Julia Meshcheryakova and Aidar Bekchintaev, competitive quiz players and runners building the smartest running community in the world.";
    if (meta) {
      meta.setAttribute("content", content);
    } else {
      const newMeta = document.createElement("meta");
      newMeta.name = "description";
      newMeta.content = content;
      document.head.appendChild(newMeta);
    }

    // JSON-LD structured data
    const jsonLd = {
      "@context": "https://schema.org",
      "@type": "SportsOrganization",
      "name": "Quizrun",
      "url": "https://quizrun.org",
      "description": "A sport where running and thinking matter equally. Run a 5K, solve logical and lateral-thinking questions, each correct answer shaves time off your score.",
      "foundingDate": "2025",
      "founder": [
        { "@type": "Person", "name": "Julia Meshcheryakova" },
        { "@type": "Person", "name": "Aidar Bekchintaev" }
      ],
      "sport": "Quizrun",
      "keywords": ["quizrun", "quiz run", "running quiz", "5K race", "lateral thinking", "logical questions"],
      "areaServed": [
        { "@type": "City", "name": "London" },
        { "@type": "City", "name": "Berlin" },
        { "@type": "City", "name": "Prague" },
        { "@type": "City", "name": "New York" },
        { "@type": "City", "name": "Tbilisi" },
        { "@type": "City", "name": "Haifa" },
        { "@type": "City", "name": "Limassol" }
      ],
      "sameAs": [
        "https://www.instagram.com/quiz_run",
        "https://www.facebook.com/groups/quizrun",
        "https://www.strava.com/clubs/quizrun",
        "https://t.me/quiz_run"
      ]
    };
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.textContent = JSON.stringify(jsonLd);
    script.id = "about-jsonld";
    document.head.appendChild(script);

    return () => {
      const el = document.getElementById("about-jsonld");
      if (el) el.remove();
    };
  }, []);
  return (
    <div>
      <PageHeader
        eyebrow="About"
        title="About Quizrun"
        intro={
          <div className="space-y-4">
            <p>
              Quizrun is a sport where running and thinking matter equally. You run, but along the way you stop to solve logical and lateral-thinking questions — each correct answer shaves time off your score.
            </p>
            <p>
              We spent a year testing different combinations of running and questions — thorough balancing calculations and live events — before landing on the current format: 2.5 km + 5 questions + 2.5 km + 5 questions, with a 2:30 bonus for each correct answer.
            </p>
          </div>
        }
      />

      <main className="container mx-auto px-4 md:px-8 pb-16">
        <div className="max-w-2xl space-y-6">
          {/* Mission */}
          <div className="rounded-xl border bg-card p-6 md:p-8">
            <div className="flex items-center gap-2 mb-3">
              <Heart className="h-5 w-5 text-primary" aria-hidden="true" />
              <h2 className="text-lg font-bold text-foreground">Our Mission</h2>
            </div>
            <p className="text-base leading-relaxed text-muted-foreground">
              Building the smartest running community in the world. Making nerds like us more physically fit — and spreading logical thinking globally.
            </p>
          </div>

          {/* Founders */}
          <div className="text-base leading-relaxed text-muted-foreground">
            <h2 className="text-lg font-bold text-foreground mb-4">The Founders</h2>

            {/* float on desktop so copy wraps the photo; full-width stack on mobile */}
            <img
              src="/images/founders.webp"
              alt="Julia and Aidar after a Quizrun event"
              className="rounded-lg w-full sm:float-left sm:w-56 sm:mr-6 sm:mb-2"
            />

            <div className="space-y-4">
              <p>
                We're Julia Meshcheryakova and Aidar Bekchintaev — a machine learning engineer and a contemporary artist. Quizrun is what we do outside of our day jobs.
              </p>
              <p>
                We both grew up playing competitive quiz games — the kind of intellectual sport with thousands of teams worldwide, where teams solve puzzles under time pressure. Aidar has been playing since 2002, Julia since 2005.
              </p>
              <p>
                Both of us started running regularly in 2024, shortly after moving to the UK — parkruns, volunteering when not fit for running, running with a couple of clubs in London. Aidar ran the Brighton Marathon, Julia is training for her first London Marathon in 2027. We love the community feeling.
              </p>
              <p>
                We quickly noticed something: we play significantly better after a Saturday parkrun. So we decided to combine our two big hobbies into one coherent thing. That's how Quizrun was born.
              </p>
            </div>
          </div>

          {/* Where — clear the float so it never wraps under the photo */}
          <div className="space-y-3 clear-both">
            <h2 className="text-lg font-bold text-foreground">Where We Are</h2>
            <p className="text-base leading-relaxed text-muted-foreground">
              <Link to="/events" className="underline hover:text-foreground transition-colors">Events</Link> happen monthly in parks across London, Berlin, Prague, New York, Tbilisi, Haifa, and Limassol.
            </p>
            <p className="text-base leading-relaxed text-muted-foreground">
              If you can't make it in person — we also developed a self-tracked virtual format where you only need a phone or watch to track your route.
            </p>
            <p className="text-base leading-relaxed text-muted-foreground">
              We're open to any ideas how to make it better or to discuss partnership of any kind. Just <a href="mailto:quizrun.org@gmail.com" className="underline hover:text-foreground transition-colors">drop us an email</a>.
            </p>
            <p className="text-base leading-relaxed text-muted-foreground">
              Want to help Quizrun grow? <Link to="/support" className="underline hover:text-foreground transition-colors">Support what we do →</Link>
            </p>
          </div>
        </div>
      </main>
    </div>
  );
};

export default About;
